<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="page">
	@let canUpload = authorizationService.hasPermission(permissions.APPLICATIONS_UPLOAD);
	@let canRename = authorizationService.hasPermission(permissions.APPLICATIONS_RENAME);
	@let canDelete = authorizationService.hasPermission(permissions.APPLICATIONS_DELETE);

	@switch (_pageState()) {
		@case (_PageStates.UPLOAD) {
			<div class="upload">
				<globalictcc-switch
					[options]="viewSwitchOptions"
					(handleSwitch)="onViewSwitch($event)"
				/>
				<div class="title">
					<span class="header">Strategic applications</span>

					<span class="description">
						@if (canUpload) {
							Upload your strategic applications report in Excel format to unlock
							tailored insights.
						} @else {
							No strategic applications report has been uploaded yet.
						}
					</span>

					<globalictcc-button
						[textOnly]="true"
						label="Download Templates (LV1 & LV2)"
						iconName="download"
						(handleClick)="onDownloadTemplate()"
					/>
				</div>

				@if (canUpload) {
					<globalictcc-upload-file
						[errorMessage]="sameNameErrorMessage()"
						[acceptedFilesExtensions]="['.xlsx']"
						(handleUpload)="onFileUpload($event)"
						(handleRemove)="onRemoveFile()"
					/>

					<globalictcc-button
						label="Upload report"
						[disabled]="isUploadDisabled()"
						(handleClick)="onConfirmUpload()"
					/>
				}
			</div>
		}
		@case (_PageStates.LOADING) {
			<div class="loading">
				<globalictcc-spinner />

				<span class="message">Loading...</span>
			</div>
		}
		@case (_PageStates.ERROR) {
			<div class="error">
				<span class="message">We couldn't load your reports right now.</span>

				<globalictcc-button
					label="Retry"
					(handleClick)="onRetry()"
				/>
			</div>
		}
		@case (_PageStates.MAIN) {
			<div class="main">
				<div class="header">
					<div class="filters-section">
						<globalictcc-switch
							[options]="viewSwitchOptions"
							(handleSwitch)="onViewSwitch($event)"
						/>

						<div class="period">
							<globalictcc-dropdown
								label="Reporting period"
								[formControl]="periodControl"
								[options]="periodOptions"
							/>
							<globalictcc-button
								label="Apply"
								(handleClick)="onApply()"
								[disabled]="!periodControl.value"
							/>
						</div>
					</div>

					<div class="title-block">
						<h1>Strategic Applications</h1>

						<div class="file-controls">
							<div class="file-dropdown">
								<globalictcc-dropdown
									[isHuge]="true"
									[options]="filesOptions"
									[formControl]="fileControl"
									(handleSelectionChange)="onFileChange($event)"
								/>
								@if (canUpload) {
									<globalictcc-button
										label="Upload file"
										(handleClick)="onUploadReport()"
									/>
								}
							</div>

							@if (canRename || canDelete) {
								<div class="actions">
									@if (canRename) {
										<globalictcc-button
											label="Rename"
											iconName="edit"
											[textOnly]="true"
											(handleClick)="onRename()"
										/>
									}

									@if (canDelete) {
										<globalictcc-button
											label="Delete"
											iconName="delete"
											[textOnly]="true"
											(handleClick)="onDelete()"
										/>
									}
								</div>
							}
						</div>
					</div>
				</div>

				@let canSeeFootprint =
					authorizationService.canSee(grants.totalApplicationsCarbonFootprint);
				@let canSeeApplicationsCount =
					authorizationService.canSee(grants.totalApplications);
				@let canSeeHosting =
					authorizationService.canSee(grants.emissionBreakdownByHostingType);
				@let canSeeScope = authorizationService.canSee(grants.emissionBreakdownByScope);

				@if (canSeeFootprint || canSeeApplicationsCount) {
					@let generalInformation = generalInformation$ | async;

					@if (generalInformation?.data; as kpis) {
						<div class="kpi">
							@if (canSeeFootprint) {
								<globalictcc-kpi
									label="Total Applications Carbon Footprint"
									[value]="kpis.footprintValue"
									[unit]="kpis.footprintUnit"
								/>
							}

							@if (canSeeApplicationsCount) {
								<globalictcc-kpi
									label="Total Applications"
									[value]="kpis.countValue"
									[unit]="kpis.countUnit"
								/>
							}
						</div>
					} @else if (generalInformation?.isLoading ?? true) {
						<div class="kpi-loading">
							<globalictcc-spinner />
						</div>
					}
				}

				@if (authorizationService.canSee(grants.totalEmissionsByApplications)) {
					@let totalEmissionsOvertimeChart = totalEmissionsOvertimeChart$ | async;

					<section class="chart-block emissions-overtime">
						<div class="section-title">
							<span class="chart-title"
								>Total emissions overtime by applications for chosen Reporting
								Period</span
							>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										totalEmissionsOvertimeChartRef.instance,
										'strategic_total_emissions_overtime'
									)
								"
							/>
						</div>

						<globalictcc-chart
							#totalEmissionsOvertimeChartRef
							chartType="line"
							[isLegendVisible]="true"
							[isTitleVisible]="false"
							[data]="totalEmissionsOvertimeChart?.data?.chart"
							[hasError]="
								totalEmissionsOvertimeChart?.hasError ||
								totalEmissionsOvertimeChart?.data?.hasError
							"
							[isLoading]="totalEmissionsOvertimeChart?.isLoading ?? true"
							(handleChartRefresh)="refreshTotalEmissionsOvertimeChart()"
						/>
					</section>
				}

				@if (canSeeHosting || canSeeScope) {
					<div class="charts-grid">
						@if (canSeeHosting) {
							@let hostingChart = hostingChart$ | async;

							<section class="chart-block">
								<div class="section-title">
									<span class="chart-title"
										>Emission Breakdown by Hosting Type</span
									>

									<globalictcc-button
										label="Export chart"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												hostingChartRef.instance,
												'strategic_hosting_breakdown'
											)
										"
									/>
								</div>

								<globalictcc-chart
									#hostingChartRef
									chartType="pie"
									labelPosition="outside"
									[isTitleVisible]="false"
									[data]="hostingChart?.data?.chart"
									[hasError]="
										hostingChart?.hasError || hostingChart?.data?.hasError
									"
									[isLoading]="hostingChart?.isLoading ?? true"
									[colors]="hostingChartColors"
									(handleChartRefresh)="refreshHostingChart()"
								/>
							</section>
						}

						@if (canSeeScope) {
							@let scopeChart = scopeChart$ | async;

							<section class="chart-block">
								<div class="section-title">
									<span class="chart-title">Emission Breakdown by Scope</span>

									<globalictcc-button
										label="Export chart"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												scopeChartRef.instance,
												'strategic_scope_breakdown'
											)
										"
									/>
								</div>

								<globalictcc-chart
									#scopeChartRef
									chartType="pie"
									labelPosition="outside"
									[isTitleVisible]="false"
									[data]="scopeChart?.data?.chart"
									[hasError]="scopeChart?.hasError || scopeChart?.data?.hasError"
									[isLoading]="scopeChart?.isLoading ?? true"
									[colors]="scopeChartColors"
									(handleChartRefresh)="refreshScopeChart()"
								/>
							</section>
						}
					</div>
				}

				@if (authorizationService.canSee(grants.top10EmissionsBreakdown)) {
					@let applicationsChart = applicationsChart$ | async;

					<section class="chart-block applications">
						<div class="section-title">
							<span class="chart-title">Top 10 emissions Breakdown</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										applicationsChartRef.instance,
										'strategic_applications_breakdown'
									)
								"
							/>
						</div>

						<globalictcc-chart
							#applicationsChartRef
							chartType="barY"
							[isGradient]="true"
							[isTitleVisible]="false"
							[data]="applicationsChart?.data?.chart"
							[hasError]="
								applicationsChart?.hasError || applicationsChart?.data?.hasError
							"
							[isLoading]="applicationsChart?.isLoading ?? true"
							[configOverrides]="applicationsChartOverrides"
							(handleChartRefresh)="refreshApplicationsChart()"
						/>
					</section>
				}

				@if (authorizationService.canSee(grants.totalApplicationsEmissions)) {
					@let environmentalImpact = environmentalImpact$ | async;

					<section class="chart-block">
						<div class="section-title">
							<span class="chart-title">Total Application Emissions</span>
						</div>

						<globalictcc-table
							[gridOptions]="environmentalImpactGridOptions"
							[hasError]="!!environmentalImpact?.hasError"
							(handleTableRefresh)="refreshEnvironmentalImpactTable()"
						/>
						<globalictcc-pagination
							[currentPage]="environmentalImpactCurrentPage"
							[pageSize]="environmentalImpactPageSize"
							[totalItems]="environmentalImpactTotalItems"
							[totalPages]="environmentalImpactTotalPages"
							[pageItems]="environmentalImpactPageItems"
							(handleNextPage)="onEnvironmentalImpactNextPage()"
							(handlePreviousPage)="onEnvironmentalImpactPreviousPage()"
						/>
					</section>
				}

				<div class="download-action">
					<globalictcc-button
						label="Download csv"
						[textOnly]="true"
						iconName="download"
						[disabled]="isDownloadDisabled()"
						(handleClick)="onDownloadFile()"
					/>
				</div>
			</div>
		}
	}
</section>

<!-- Dialogs content -->
<ng-template #uploadReport>
	<div class="upload-dialog">
		<span class="description">
			Upload your strategic applications report in Excel format. Use the template from your
			data source as a reference to make sure the columns align with the expected structure.
		</span>

		<globalictcc-button
			[textOnly]="true"
			label="Download Templates (LV1 & LV2)"
			iconName="download"
			(handleClick)="onDownloadTemplate()"
		/>

		<globalictcc-upload-file
			[errorMessage]="sameNameErrorMessage()"
			[fluid]="true"
			[acceptedFilesExtensions]="['.xlsx']"
			(handleUpload)="onFileUpload($event)"
		/>
	</div>
</ng-template>

<ng-template #rename>
	<div class="rename-input-container">
		<globalictcc-input
			[formControl]="renameFileControl"
			[label]="'New name'"
			[customErrorMessage]="sameNameErrorMessage()"
			(handleInputChange)="onRenameChange($event)"
		/>
	</div>
</ng-template>
